/**
 * Grant Management Admin Interface
 *
 * Provides comprehensive grant management functionality including:
 * - Grants: View, create, edit, and delete grants with v5.002 schema support
 * - Resource Permissions: Interactive grid for viewing resource permissions from a resource-centric perspective
 * - User Permissions: View effective permissions for specific users from a user-centric perspective
 */

import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Paper,
  Typography,
  Tabs,
  Tab,
  Alert,
  CircularProgress,
  Snackbar
} from '@mui/material';
import { Security, VerifiedUser, Visibility } from '@mui/icons-material';

// Layout
import DashboardLayout from 'layouts/DashboardLayout'
import DashboardNavbar from 'components/RDNavbars/DashboardNavbar';

// Components
import PermissionViewerTab from './components/PermissionViewerTab';
import GrantList from './components/GrantList';
import UserPermissionsTab from './components/UserPermissionsTab';
import ClientSelector from '../../../../components/common/admin/ClientSelector';

// Services (imports kept for potential future use)

// Hooks
import { useGrants } from './hooks/useGrants';
import { usePermissionValidation } from './hooks/usePermissionValidation';
import { useUser } from '../../../../hooks/useUser';
import { useAdminContext } from '../../../../context/AdminContext';

// Utils
import { validateGrantAccess } from './utils/grantHelpers';

function TabPanel({ children, value, index, ...other }) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`grant-tabpanel-${index}`}
      aria-labelledby={`grant-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

export default function GrantAdmin() {
  const { user } = useUser();
  const {
    selectedClient,
    setSelectedClient,
    isAppAdmin,
    availableClients,
    loading: adminLoading,
    error: adminError
  } = useAdminContext();
  const [activeTab, setActiveTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Debug: log user object to see what clientId field is called
  useEffect(() => {
    if (user) {
      console.log('GrantAdmin user object:', user);
      console.log('user.client:', user.client);
    }
  }, [user]);

  // Custom hooks
  // Use selectedClient from AdminContext for fetching grants
  const {
    grants,
    isLoading: grantsLoading,
    error: grantsError,
    refetch: refetchGrants,
    createGrant,
    updateGrant,
    deleteGrant
  } = useGrants(selectedClient);

  const {
    validateGrant,
    isValidating,
    validationResult
  } = usePermissionValidation();

  useEffect(() => {
    // Check user permissions
    if (user && !validateGrantAccess(user)) {
      setError('You do not have permission to manage grants');
      setLoading(false);
      return;
    }
    setLoading(false);
  }, [user]);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleGrantCreate = async (grantData) => {
    try {
      console.log('handleGrantCreate called with:', grantData);
      setLoading(true);

      // TODO: Fix permValidateGrantQuery Lambda function (aws-sdk dependency issue)
      // Temporarily skip validation to unblock grant creation
      /*
      const validation = await validateGrant(grantData, 'CREATE', grantData.clientId);
      console.log('Validation result:', validation);
      if (!validation.isValid) {
        const errorMsg = `Validation failed: ${validation.errors.map(e => e.message).join(', ')}`;
        console.error(errorMsg);
        setError(errorMsg);
        return;
      }
      */

      console.log('Creating grant...');
      const result = await createGrant(grantData);
      console.log('Grant created:', result);
      setSuccess('Grant created successfully');
      await refetchGrants();
    } catch (err) {
      console.error('Error creating grant:', err);
      setError(`Failed to create grant: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleGrantUpdate = async (grantId, updates) => {
    try {
      setLoading(true);

      // TODO: Fix permValidateGrantQuery Lambda function (aws-sdk dependency issue)
      // Temporarily skip validation to unblock grant updates
      /*
      const mergedGrant = { ...selectedGrant, ...updates };
      const validation = await validateGrant(mergedGrant, 'UPDATE', mergedGrant.clientId);
      if (!validation.isValid) {
        setError(`Validation failed: ${validation.errors.map(e => e.message).join(', ')}`);
        return;
      }
      */

      const result = await updateGrant(grantId, updates);
      setSuccess('Grant updated successfully');
      await refetchGrants();
    } catch (err) {
      setError(`Failed to update grant: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleGrantDelete = async (grantId) => {
    try {
      setLoading(true);
      const result = await deleteGrant(grantId);
      setSuccess('Grant deleted successfully');
      await refetchGrants();
    } catch (err) {
      setError(`Failed to delete grant: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };


  return (
    <DashboardLayout>
      <DashboardNavbar />
      {(loading || grantsLoading) ? (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      ) : (error || grantsError) ? (
        <Box p={3}>
          <Alert severity="error">{error || grantsError}</Alert>
        </Box>
      ) : (
        <Box sx={{ width: '100%', p: 3 }}>
          <Paper elevation={3}>
            <Box sx={{ p: 3, borderBottom: 1, borderColor: 'divider' }}>
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Box>
                  <Typography variant="h4" component="h1" gutterBottom>
                    Grant Management
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Manage permissions and access control through grants
                  </Typography>
                </Box>
                {isAppAdmin && (
                  <ClientSelector
                    value={selectedClient}
                    onChange={setSelectedClient}
                    clients={availableClients}
                    loading={adminLoading}
                    error={adminError}
                  />
                )}
              </Box>
            </Box>

            <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
              <Tabs
                value={activeTab}
                onChange={handleTabChange}
                aria-label="grant management tabs"
                variant="scrollable"
                scrollButtons="auto"
              >
                <Tab icon={<Security />} label="Grants" />
                <Tab icon={<Visibility />} label="Resource Permissions" />
                <Tab icon={<VerifiedUser />} label="User Permissions" />
              </Tabs>
            </Box>

            <TabPanel value={activeTab} index={0}>
              <GrantList
                grants={grants}
                onDelete={handleGrantDelete}
                onRefresh={refetchGrants}
                onSave={async (grantData) => {
                  if (grantData.id) {
                    // Update existing grant
                    await handleGrantUpdate(grantData.id, grantData);
                  } else {
                    // Create new grant
                    await handleGrantCreate(grantData);
                  }
                }}
                user={user}
                selectedClient={selectedClient}
                availableClients={availableClients}
              />
            </TabPanel>

            <TabPanel value={activeTab} index={1}>
              <PermissionViewerTab clientId={selectedClient} grants={grants} />
            </TabPanel>

            <TabPanel value={activeTab} index={2}>
              <UserPermissionsTab />
            </TabPanel>
          </Paper>

          <Snackbar
            open={!!success}
            autoHideDuration={6000}
            onClose={() => setSuccess(null)}
          >
            <Alert onClose={() => setSuccess(null)} severity="success">
              {success}
            </Alert>
          </Snackbar>
        </Box>
      )}
    </DashboardLayout>
  );
}